"use client";

import { message } from "antd";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useRef, useState } from "react";
import type { CaptchaComponentRef } from "./page";

type PendingVerification = {
	requestId: number;
	resolve: (captchaToken: string | null) => void;
};

export function useFormCaptcha() {
	const translate = useTranslations("form");
	const captchaRef = useRef<CaptchaComponentRef | null>(null);
	const submissionInProgressRef = useRef(false);
	const pendingUploadCountRef = useRef(0);
	const uploadQueueRef = useRef<Promise<void>>(Promise.resolve());
	const mountedRef = useRef(false);
	const requestSequenceRef = useRef(0);
	const activeRequestRef = useRef<number | null>(null);
	const pendingVerificationRef = useRef<PendingVerification | null>(null);
	const [captchaRequestId, setCaptchaRequestId] = useState(0);
	const [captchaToken, setCaptchaToken] = useState("");
	const [isCaptchaVisible, setIsCaptchaVisible] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isUploading, setIsUploading] = useState(false);

	useEffect(() => {
		mountedRef.current = true;
		return () => {
			mountedRef.current = false;
			activeRequestRef.current = null;
			pendingVerificationRef.current?.resolve(null);
			pendingVerificationRef.current = null;
		};
	}, []);

	const beginSubmission = () => {
		if (submissionInProgressRef.current) return null;
		if (pendingUploadCountRef.current > 0) {
			void message.warning(translate("pleaseWaitUpload"));
			return null;
		}
		const currentToken = captchaRef.current?.getToken();
		if (!currentToken) {
			void message.warning(translate("captchaRequired"));
			return null;
		}
		submissionInProgressRef.current = true;
		setIsSubmitting(true);
		// Tokens are single-use, including when the server rejects a request.
		captchaRef.current?.reset("chart");
		return currentToken;
	};

	const finishSubmission = useCallback(() => {
		const wasDeferredSubmission = activeRequestRef.current !== null;
		activeRequestRef.current = null;
		const pendingVerification = pendingVerificationRef.current;
		pendingVerificationRef.current = null;
		pendingVerification?.resolve(null);
		submissionInProgressRef.current = false;
		if (!mountedRef.current) return;
		setIsCaptchaVisible(false);
		setCaptchaToken("");
		// Deferred widgets are unmounted; only the always-visible questionnaire widget needs a reset.
		if (!wasDeferredSubmission) captchaRef.current?.reset("chart");
		setIsSubmitting(false);
	}, []);

	const handleCaptchaToken = useCallback(
		(nextToken: string) => {
			const pendingVerification = pendingVerificationRef.current;
			if (
				!nextToken ||
				!mountedRef.current ||
				!pendingVerification ||
				activeRequestRef.current !== captchaRequestId ||
				pendingVerification.requestId !== captchaRequestId
			) return;
			// Consume the pending callback before resuming; duplicates and old widgets cannot send again.
			pendingVerificationRef.current = null;
			setIsCaptchaVisible(false);
			pendingVerification.resolve(nextToken);
		},
		[captchaRequestId],
	);

	const handleCaptchaFailure = useCallback(() => {
		if (!mountedRef.current || pendingVerificationRef.current?.requestId !== captchaRequestId) return;
		finishSubmission();
		void message.error(translate("captchaLoadFailed"));
	}, [captchaRequestId, finishSubmission, translate]);

	const requestSubmissionToken = async (validateContent?: () => Promise<boolean>): Promise<string | null> => {
		if (submissionInProgressRef.current) return null;
		if (pendingUploadCountRef.current > 0) {
			void message.warning(translate("pleaseWaitUpload"));
			return null;
		}
		submissionInProgressRef.current = true;
		setIsSubmitting(true);
		const requestId = ++requestSequenceRef.current;
		activeRequestRef.current = requestId;
		try {
			const isContentValid = validateContent ? await validateContent() : true;
			if (!mountedRef.current || activeRequestRef.current !== requestId) return null;
			if (!isContentValid) {
				finishSubmission();
				return null;
			}
			const verifiedToken = await new Promise<string | null>((resolve) => {
				pendingVerificationRef.current = { requestId, resolve };
				setCaptchaRequestId(requestId);
				setIsCaptchaVisible(true);
			});
			return mountedRef.current && activeRequestRef.current === requestId ? verifiedToken : null;
		} catch {
			if (mountedRef.current && activeRequestRef.current === requestId) {
				finishSubmission();
				void message.error(translate("sendFail"));
			}
			return null;
		}
	};

	const runUpload = (uploadFiles: () => Promise<void>) => {
		if (submissionInProgressRef.current) {
			return Promise.reject(new Error(translate("captchaSubmissionInProgress")));
		}
		pendingUploadCountRef.current += 1;
		setIsUploading(true);

		// Track queued uploads to prevent submission before all attachments are ready.
		const uploadRequest = uploadQueueRef.current.then(async () => {
			try {
				await uploadFiles();
			} finally {
				pendingUploadCountRef.current -= 1;
				if (pendingUploadCountRef.current === 0) {
					setIsUploading(false);
				}
			}
		});
		uploadQueueRef.current = uploadRequest.catch(() => undefined);
		return uploadRequest;
	};

	return {
		captchaRef,
		setCaptchaToken,
		captchaRequestId,
		handleCaptchaToken,
		handleCaptchaFailure,
		isSubmitting,
		isUploading,
		isCaptchaVisible,
		canSubmit: Boolean(captchaToken) && !isSubmitting && !isUploading,
		canRequestSubmission: !isSubmitting && !isUploading,
		beginSubmission,
		requestSubmissionToken,
		finishSubmission,
		runUpload,
	};
}
